import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Calendar as CalendarIcon, MapPin, Clock, Users, ExternalLink, Video } from 'lucide-react';
import { Calendar } from '../components/ui/calendar';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EventsPage = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const response = await axios.get(`${API_URL}/events`);
        setEvents(response.data);
      } catch (error) {
        console.error('Error fetching events:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchEvents();
  }, []);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const eventDates = events.map(e => new Date(e.date));

  return (
    <div className="min-h-screen bg-cream-100 py-12" data-testid="events-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-royal-700 mb-4">
            Community Events
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Discover Gurpurabs, community gatherings, and spiritual events near you and worldwide.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Calendar */}
          <div className="lg:col-span-1">
            <Card className="sticky top-24">
              <CardContent className="p-4">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={setSelectedDate}
                  modifiers={{ hasEvent: eventDates }}
                  modifiersClassNames={{
                    hasEvent: 'bg-saffron-100 text-saffron-800 font-bold'
                  }}
                  className="rounded-md border-0"
                />
              </CardContent>
            </Card>
          </div>

          {/* Events List */}
          <div className="lg:col-span-2 space-y-6">
            {loading ? (
              <>
                {[1, 2, 3].map((i) => (
                  <Card key={i} className="animate-pulse">
                    <CardContent className="p-6">
                      <div className="h-6 bg-gray-200 rounded w-3/4 mb-4"></div>
                      <div className="h-4 bg-gray-200 rounded w-full mb-2"></div>
                      <div className="h-4 bg-gray-200 rounded w-2/3"></div>
                    </CardContent>
                  </Card>
                ))}
              </>
            ) : events.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <CalendarIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-xl font-medium text-gray-600">No upcoming events</h3>
                  <p className="text-gray-500">Check back soon for community events</p>
                </CardContent>
              </Card>
            ) : (
              events.map((event) => (
                <Card 
                  key={event.id} 
                  className="card-hover bg-white border-gray-100 overflow-hidden"
                  data-testid={`event-card-${event.id}`}
                >
                  <CardContent className="p-0">
                    <div className="flex flex-col md:flex-row">
                      {/* Date Badge */}
                      <div className="md:w-32 bg-gradient-to-br from-saffron-500 to-gold-500 p-4 flex flex-col items-center justify-center text-white">
                        <span className="text-4xl font-bold">
                          {new Date(event.date).getDate()}
                        </span>
                        <span className="text-sm uppercase">
                          {new Date(event.date).toLocaleDateString('en-US', { month: 'short' })}
                        </span>
                        <span className="text-xs mt-1">
                          {new Date(event.date).getFullYear()}
                        </span>
                      </div>
                      
                      {/* Event Details */}
                      <div className="flex-1 p-6">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              {event.is_virtual && (
                                <Badge className="bg-green-100 text-green-700 border-green-200">
                                  <Video className="w-3 h-3 mr-1" />
                                  Virtual
                                </Badge>
                              )}
                              <Badge variant="outline" className="border-saffron-200 text-saffron-700">
                                {event.organizer}
                              </Badge>
                            </div>
                            
                            <h3 className="font-heading text-xl font-semibold text-gray-900 mb-2">
                              {event.title}
                            </h3>
                            
                            <p className="text-gray-600 mb-4 line-clamp-2">
                              {event.description}
                            </p>
                            
                            <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                              <span className="flex items-center gap-1">
                                <Clock className="w-4 h-4 text-saffron-500" />
                                {event.time}
                              </span>
                              <span className="flex items-center gap-1">
                                <MapPin className="w-4 h-4 text-saffron-500" />
                                {event.location}
                              </span>
                            </div>
                          </div>
                        </div>
                        
                        {event.is_virtual && event.virtual_link && (
                          <div className="mt-4">
                            <a
                              href={event.virtual_link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-2 text-royal-600 hover:text-royal-700 font-medium"
                            >
                              <ExternalLink className="w-4 h-4" />
                              Join Virtual Event
                            </a>
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventsPage;
